import type {
	ArquivoKind,
	ArquivoMaterial,
	ArquivoSource,
	Cliente,
} from "../../../types/gerArquivos";

export interface ClienteDisplay {
	destaque: string;
	secundarios: string[];
}

const FALLBACK_DESTAQUE = "Sem cliente";
const FALLBACK_DATA = "Data não informada";

function normalize(value: unknown): string {
	if (value == null) return "";
	return String(value).trim();
}

/**
 * Formata uma data ISO no padrão pt-BR (dd/mm/aaaa, hh:mm) usado no Gerenciador.
 * Centraliza o `toLocaleString` antes duplicado no card, na tabela e no modal.
 * Retorna um fallback quando a data é ausente ou inválida.
 */
export function formatArquivoData(dataIso: string | undefined | null): string {
	if (!dataIso) return FALLBACK_DATA;
	const data = new Date(dataIso);
	if (Number.isNaN(data.getTime())) return FALLBACK_DATA;
	return data.toLocaleString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/**
 * Monta a exibição hierárquica do cliente no card:
 * - destaque: preferência nome fantasia > razão social > nome
 * - secundários: os demais nomes distintos do destaque
 *
 * Linhas duplicadas (ex.: fantasia === razão social) são suprimidas.
 */
export function buildClienteDisplay(
	cliente: Cliente | undefined | null,
): ClienteDisplay {
	if (!cliente) {
		return { destaque: FALLBACK_DESTAQUE, secundarios: [] };
	}

	const nome = normalize(cliente.nome);
	const nomeFantasia = normalize(
		cliente.nomeFantasia ?? cliente.empresa ?? cliente.nomeFantasia,
	);
	const razaoSocial = normalize(cliente.razaoSocial);

	// Ordem de preferência para o destaque: fantasia > razão social > nome
	let destaque = "";
	if (nomeFantasia) destaque = nomeFantasia;
	else if (razaoSocial) destaque = razaoSocial;
	else if (nome) destaque = nome;

	if (!destaque) {
		return { destaque: FALLBACK_DESTAQUE, secundarios: [] };
	}

	const candidatos = [nomeFantasia, razaoSocial, nome].filter(
		(v) => v && v !== destaque,
	);
	const secundarios = [...new Set(candidatos)];

	// Se o destaque veio do nome (sem fantasia/razão), não há secundários úteis.
	if (!nomeFantasia && !razaoSocial) {
		return { destaque: nome || FALLBACK_DESTAQUE, secundarios: [] };
	}

	return { destaque, secundarios };
}

/**
 * Resume os materiais (qualidades) de um arquivo em uma string curta.
 * Nomes nulos/vazios são ignorados; duplicados são removidos.
 */
export interface PedidoItemRef {
	orcId: number;
	oitId?: number;
}

/**
 * Resolve referência de pedido/item quando o arquivo já possui orçamento vinculado.
 * Prioriza `oitId` na raiz do DTO; fallback em `materiais[0]` apenas para legado.
 */
export function resolvePedidoItemRef(arquivo: {
	orcId?: number;
	oitId?: number;
	materiais?: ArquivoMaterial[];
}): PedidoItemRef | null {
	const orcId = arquivo.orcId;
	if (orcId == null || !Number.isFinite(orcId) || orcId <= 0) {
		return null;
	}

	const rootOitId = arquivo.oitId;
	if (rootOitId != null && Number.isFinite(rootOitId) && rootOitId > 0) {
		return { orcId, oitId: rootOitId };
	}

	const legacyOitId = arquivo.materiais?.[0]?.oitId;
	if (legacyOitId != null && Number.isFinite(legacyOitId) && legacyOitId > 0) {
		return { orcId, oitId: legacyOitId };
	}

	return { orcId };
}

/** Badge de origem na listagem: RASCUNHO (pre) ou PEDIDO (cdr_file_save). */
export function getOrigemLabel(arquivo: {
	kind?: ArquivoKind;
	source?: ArquivoSource;
}): string | null {
	if (arquivo.kind === "producao" || arquivo.source === "cdr_save") {
		return "PEDIDO";
	}
	if (
		arquivo.kind === "gerenciador" ||
		arquivo.kind === "rascunho" ||
		arquivo.source === "pre"
	) {
		return "RASCUNHO";
	}
	return null;
}

/** Status "salvo" é redundante na lista; só exibir estados operacionais. */
export function shouldShowArquivoStatusBadge(
	status: string | undefined | null,
): boolean {
	return status === "em_edicao" || status === "finalizado";
}

export function getOrigemBadgeClass(arquivo: {
	kind?: ArquivoKind;
	source?: ArquivoSource;
}): string {
	if (arquivo.kind === "producao" || arquivo.source === "cdr_save") {
		return "bg-purple-500/20 text-purple-300 border-purple-500/30";
	}
	return "bg-cyan-500/20 text-cyan-300 border-cyan-500/30";
}

export function buildMaterialSummary(
	materiais: ArquivoMaterial[] | undefined | null,
): string {
	if (!materiais || materiais.length === 0) return "";

	const nomes = materiais
		.map((m) => normalize(m.nome))
		.filter((n) => n.length > 0);

	const unicos = [...new Set(nomes)];
	return unicos.join(", ");
}
